import React, { useEffect, useRef } from "react";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import { cityCoords, interpolate } from "@/lib/geo";

// Fix default icons
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon-2x.png",
  iconUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon.png",
  shadowUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-shadow.png",
});

const truckIcon = L.divIcon({
  className: "",
  html: `<div style="background:#D38138;color:white;width:30px;height:30px;border-radius:50%;display:grid;place-items:center;box-shadow:0 4px 14px rgba(217,119,6,0.55);border:3px solid white;font-size:16px;">🚚</div>`,
  iconSize: [30, 30],
  iconAnchor: [15, 15],
});

const TrackingMap = ({ pickup, drop, progressPercent = 0, lastLocation = null }) => {
  const mapRef = useRef(null);
  const containerRef = useRef(null);
  const layersRef = useRef({});

  const p = cityCoords(pickup);
  const d = cityCoords(drop);

  useEffect(() => {
    if (!containerRef.current || mapRef.current) return;
    const center = p && d ? interpolate(p, d, 0.5) : [22.5, 78.9];
    const map = L.map(containerRef.current, {
      zoomControl: false,
      attributionControl: false,
      scrollWheelZoom: false,
      dragging: true,
    }).setView(center, p && d ? 5 : 4);
    L.tileLayer("https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png", { maxZoom: 18 }).addTo(map);
    mapRef.current = map;
    return () => { map.remove(); mapRef.current = null; };
  }, []);

  useEffect(() => {
    const map = mapRef.current;
    if (!map || !p || !d) return;
    // Clear previous layers
    Object.values(layersRef.current).forEach(l => map.removeLayer(l));
    layersRef.current = {};

    layersRef.current.pickup = L.marker(p).addTo(map).bindTooltip(`Pickup · ${pickup}`);
    layersRef.current.drop = L.marker(d).addTo(map).bindTooltip(`Drop · ${drop}`);
    layersRef.current.line = L.polyline([p, d], { color: "#94a3b8", weight: 3, dashArray: "8, 8" }).addTo(map);

    const t = Math.max(0, Math.min(1, progressPercent / 100));
    const cur = lastLocation ? [lastLocation.lat, lastLocation.lng] : interpolate(p, d, t);
    layersRef.current.truck = L.marker(cur, { icon: truckIcon }).addTo(map);
    layersRef.current.progLine = L.polyline([p, cur], { color: "#D38138", weight: 4 }).addTo(map);

    map.fitBounds(L.latLngBounds([p, d]).pad(0.3));
  }, [pickup, drop, progressPercent, lastLocation]);

  if (!p || !d) {
    return (
      <div className="rounded-2xl border border-slate-200 bg-white p-6 text-center">
        <div className="text-sm text-slate-500">Map preview unavailable for these cities.</div>
        <div className="mt-3 h-2 bg-slate-100 rounded-full overflow-hidden">
          <div className="h-full bg-amber-500 transition-all" style={{ width: `${progressPercent}%` }}/>
        </div>
        <div className="mt-2 text-xs text-slate-500">Progress · {progressPercent}%</div>
      </div>
    );
  }

  return (
    <div className="rounded-2xl overflow-hidden border border-slate-200">
      <div ref={containerRef} style={{ height: 260, width: "100%" }} data-testid="tracking-map"/>
      <div className="p-3 bg-white">
        <div className="h-2 bg-slate-100 rounded-full overflow-hidden">
          <div className="h-full bg-amber-500 transition-all duration-500" style={{ width: `${progressPercent}%` }}/>
        </div>
        <div className="mt-2 flex justify-between text-xs text-slate-500">
          <span>{pickup}</span>
          <span className="font-mono font-semibold text-amber-700">{progressPercent}%</span>
          <span>{drop}</span>
        </div>
      </div>
    </div>
  );
};

export default TrackingMap;
